import { useEffect, useMemo, useRef, useState } from "react";

import { CoreService } from "@/services/core/CoreService";
import { AnalyticsServiceV2, FrequentOutOfStock } from "@/services/features/AnalyticsServiceV2";
import InventoryService, {
  type InventoryItem,
} from "@/services/features/inventory/InventoryService";
import { DateRange, storePerformanceService } from "@/services/StorePerformanceService";
import { Store } from "@/types/store";
import { getBrowserTimezone } from "@/utils/storeTime";

export interface EnrichedFrequentOutOfStock {
  storeId: number | null;
  customerId: number | null;
  productId: string | null;
  color: string | null;
  size: string | null;
  size2: string | null;
  count: number;
  productTotalCount: number;
  productName: string | null;
  imageUrl: string | null;
  price: string | null;
}

export interface EnrichedOutOfStockReport {
  outOfStockCount: number;
  recoveryCount: number;
  frequentOutOfStocks: EnrichedFrequentOutOfStock[];
}

export type OutOfStockTrend =
  | { status: "loading" }
  | { status: "unavailable" }
  | {
      status: "ready";
      percent?: number;
      absoluteChange?: number;
      previousValue: number;
      direction: "up" | "down" | "flat";
      isGood: boolean;
    };

interface OutOfStockComparisonData {
  outOfStockCount: number;
  recoveryCount: number;
}

const emptyReport: EnrichedOutOfStockReport = {
  outOfStockCount: 0,
  recoveryCount: 0,
  frequentOutOfStocks: [],
};

const createTrend = (
  current: number | null | undefined,
  previous: number | null | undefined,
  lowerIsBetter: boolean,
): OutOfStockTrend => {
  if (current == null || previous === null) {
    return { status: "unavailable" };
  }

  if (previous === undefined) {
    return { status: "loading" };
  }

  if (current === previous) {
    return {
      status: "ready",
      percent: 0,
      previousValue: previous,
      direction: "flat",
      isGood: true,
    };
  }

  if (previous === 0) {
    const absoluteChange = current;
    return {
      status: "ready",
      absoluteChange,
      previousValue: previous,
      direction: absoluteChange > 0 ? "up" : "down",
      isGood: lowerIsBetter ? absoluteChange < 0 : absoluteChange > 0,
    };
  }

  const percent = ((current - previous) / Math.abs(previous)) * 100;
  const direction = percent > 0 ? "up" : "down";
  return {
    status: "ready",
    percent: Math.abs(percent),
    previousValue: previous,
    direction,
    isGood: lowerIsBetter ? percent < 0 : percent > 0,
  };
};

const toEnrichedRow = (
  row: FrequentOutOfStock,
  sku?: InventoryItem | null,
): EnrichedFrequentOutOfStock => ({
  ...row,
  productName: sku?.title || null,
  imageUrl: sku?.imageUrl || null,
  price: sku?.salePrice || sku?.price || null,
});

const findProductSku = async (
  inventoryService: InventoryService,
  storeId: number,
  productId: string,
): Promise<InventoryItem | null> => {
  try {
    const sku = await inventoryService.getFirstSkuByProductId(storeId, productId);
    if (sku) {
      return sku;
    }
  } catch {
    // Product enrichment is best-effort and should not block the report.
  }

  return null;
};

export const useOutOfStockReport = (
  stores: Store[] | null,
  dateRange?: DateRange,
  comparisonDateRange?: DateRange,
) => {
  const browserTimezone = getBrowserTimezone();
  const [data, setData] = useState<EnrichedOutOfStockReport>(emptyReport);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState<OutOfStockComparisonData | null>();
  const [error, setError] = useState<string | null>(null);
  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(null);
  const activeRequestIdRef = useRef(0);
  const activeComparisonRequestIdRef = useRef(0);
  const storeIdsKey = stores?.map((store) => store.id).join(",") ?? "";
  const storeIds = useMemo(
    () => (storeIdsKey ? storeIdsKey.split(",").map(Number) : []),
    [storeIdsKey],
  );
  const startDate = storePerformanceService.formatDate(dateRange?.start, browserTimezone);
  const endDate = storePerformanceService.formatDate(dateRange?.end, browserTimezone);
  const comparisonStartDate = storePerformanceService.formatDate(
    comparisonDateRange?.start,
    browserTimezone,
  );
  const comparisonEndDate = storePerformanceService.formatDate(
    comparisonDateRange?.end,
    browserTimezone,
  );
  const hasRequestInputs = Boolean(storeIdsKey && startDate && endDate);
  const hasComparisonInputs = Boolean(comparisonStartDate && comparisonEndDate);
  const requestKey = hasRequestInputs ? `${storeIdsKey}:${startDate}:${endDate}` : null;
  const comparisonRequestKey = hasComparisonInputs
    ? `${storeIdsKey}:${comparisonStartDate}:${comparisonEndDate}`
    : null;

  useEffect(() => {
    activeRequestIdRef.current += 1;

    if (!requestKey) {
      setData(emptyReport);
      setLoading(false);
      setTableLoading(false);
      setError(null);
      setCompletedRequestKey(null);
      return;
    }

    const requestId = activeRequestIdRef.current;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setTableLoading(true);
      setError(null);
      setData(emptyReport);

      try {
        const analyticsService = AnalyticsServiceV2.getInstance();
        const params = {
          storeIds,
          startDate,
          endDate,
        };
        const isActive = () => !cancelled && activeRequestIdRef.current === requestId;

        const outOfStockCountPromise = analyticsService
          .getOutOfStockCount(params)
          .then((response) => {
            if (isActive()) {
              setData((previousData) => ({
                ...previousData,
                outOfStockCount: response.data ?? 0,
              }));
            }
          });

        const recoveryCountPromise = analyticsService
          .getOutOfStockRecoveryCount(params)
          .then((response) => {
            if (isActive()) {
              setData((previousData) => ({
                ...previousData,
                recoveryCount: response.data ?? 0,
              }));
            }
          });

        const frequentOutOfStocksPromise = analyticsService
          .getFrequentOutOfStocks(params)
          .then((response) => {
            const rows = response.data ?? [];

            if (isActive()) {
              setData((previousData) => ({
                ...previousData,
                frequentOutOfStocks: rows.map((row) => toEnrichedRow(row)),
              }));
            }

            return rows;
          });

        const reportRequestsPromise = Promise.all([
          outOfStockCountPromise,
          recoveryCountPromise,
          frequentOutOfStocksPromise,
        ]).then(() => {
          if (isActive()) {
            setLoading(false);
          }
        });

        await reportRequestsPromise;

        const frequentOutOfStocks = await frequentOutOfStocksPromise;
        const token = CoreService.getInstance().getAuthorizationHeader();
        const inventoryService = token ? new InventoryService(token) : null;
        const skuCache = new Map<string, InventoryItem | null>();
        const enrichedRows: EnrichedFrequentOutOfStock[] = [];

        for (const row of frequentOutOfStocks) {
          if (!isActive()) {
            return;
          }

          if (!inventoryService || row.storeId == null || !row.productId) {
            enrichedRows.push(toEnrichedRow(row));
            continue;
          }

          const cacheKey = `${row.storeId}:${row.productId}`;
          if (!skuCache.has(cacheKey)) {
            skuCache.set(
              cacheKey,
              await findProductSku(inventoryService, row.storeId, row.productId),
            );
          }

          enrichedRows.push(toEnrichedRow(row, skuCache.get(cacheKey)));
        }

        if (!isActive()) {
          return;
        }

        setData((previousData) => ({
          ...previousData,
          frequentOutOfStocks: enrichedRows,
        }));
        setTableLoading(false);
      } catch (err) {
        if (cancelled || activeRequestIdRef.current !== requestId) {
          return;
        }

        setError(err instanceof Error ? err.message : "Failed to load out of stock report");
        setData(emptyReport);
        setComparisonData(null);
      } finally {
        if (!cancelled && activeRequestIdRef.current === requestId) {
          setLoading(false);
          setTableLoading(false);
          setCompletedRequestKey(requestKey);
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [endDate, requestKey, startDate, storeIds]);

  useEffect(() => {
    activeComparisonRequestIdRef.current += 1;

    if (!requestKey || !comparisonRequestKey || !comparisonStartDate || !comparisonEndDate) {
      setComparisonData(null);
      return;
    }

    const requestId = activeComparisonRequestIdRef.current;
    let cancelled = false;
    const isActive = () => !cancelled && activeComparisonRequestIdRef.current === requestId;

    const fetchComparisonData = async () => {
      setComparisonData(undefined);

      try {
        const analyticsService = AnalyticsServiceV2.getInstance();
        const params = {
          storeIds,
          startDate: comparisonStartDate,
          endDate: comparisonEndDate,
        };
        const outOfStockCount = await analyticsService.getOutOfStockCount(params);
        const recoveryCount = await analyticsService.getOutOfStockRecoveryCount(params);

        if (isActive()) {
          setComparisonData({
            outOfStockCount: outOfStockCount.data ?? 0,
            recoveryCount: recoveryCount.data ?? 0,
          });
        }
      } catch {
        if (isActive()) {
          setComparisonData(null);
        }
      }
    };

    void fetchComparisonData();

    return () => {
      cancelled = true;
    };
  }, [comparisonEndDate, comparisonRequestKey, comparisonStartDate, requestKey, storeIds]);

  const isRequestPending = hasRequestInputs && completedRequestKey !== requestKey;
  const recoveryRate =
    data.outOfStockCount > 0 ? (data.recoveryCount / data.outOfStockCount) * 100 : null;
  const comparisonRecoveryRate =
    comparisonData === undefined
      ? undefined
      : comparisonData && comparisonData.outOfStockCount > 0
        ? (comparisonData.recoveryCount / comparisonData.outOfStockCount) * 100
        : null;

  return {
    data,
    loading: loading || isRequestPending,
    tableLoading: tableLoading || isRequestPending,
    error,
    outOfStockTrend: hasComparisonInputs
      ? createTrend(
          data.outOfStockCount,
          comparisonData === undefined ? undefined : (comparisonData?.outOfStockCount ?? null),
          true,
        )
      : { status: "unavailable" as const },
    recoveryTrend: hasComparisonInputs
      ? createTrend(recoveryRate, comparisonRecoveryRate, false)
      : { status: "unavailable" as const },
  };
};
